import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { IconButton, styled } from "@mui/material";
import { makeStyles, createStyles } from "@material-ui/core";
import CachedIcon from "@material-ui/icons/Cached";
import { ColorManagementIcon, InventoryIcon } from "../../assets";
import { calculateIconSize } from "../../utils/Constants";
import { setModalState } from "../../store/modal/ModalActions";
import { ColorManagementModalId } from "../color-mangement-modal/ColorManagement";
import { itemManagementModalId } from "../item-management/ItemManagementModal";
import { itemManagementSFCCModalId } from "../item-management-SFCC/ItemManagementSFCCModal";
import { refreshProductFromList } from "../../store/product-list/ProductListActions";
import {
  selectCurrentLocale,
  selectCurrentStoreId,
} from "../../store/store-list/StoreListSelectors";
import { selectCurrentCatalogId } from "../../store/catalog/CatalogSelectors";
import { selectConfigValue } from "../../store/app-config/AppConfigSelectors";
import AppState from "../../store/AppState";
import { Product } from "../../store/product/ProductTypes";
import { createStaticViewContextValue } from "../../context/view-context";
import { selectProductsColorDataForFindProducts } from "../../store/find-products/FindProductSelectors";

/** Class on the action stack; parent Paper uses `&:hover .${FIND_PRODUCTS_TILE_HOVER_ACTIONS_CLASS}` */
export const FIND_PRODUCTS_TILE_HOVER_ACTIONS_CLASS =
  "find-products-tile-hover-actions";

const StyledInventoryIcon = styled(InventoryIcon)`
  &:hover path {
    fill: #f18221;
  }
`;

interface StyleProps {
  productActionFZ: string;
  productActionDimensions: string;
  column: number;
}

const useStyles = makeStyles(() =>
  createStyles({
    actionContainer: {
      position: "absolute",
      right: "0.5em",
      top: "0.9em",
      zIndex: 20,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      opacity: 0,
      pointerEvents: "none",
      transition: "opacity 0.12s ease",
      "& .product-tile__actions": {
        display: "grid",
        justifyContent: "center",
        alignContent: "center",
        fontSize: (props: StyleProps) => props.productActionFZ,
        height: (props: StyleProps) => props.productActionDimensions,
        width: (props: StyleProps) => props.productActionDimensions,
        "& svg": {
          width: (props) => `${calculateIconSize(props.column)}rem`,
          height: (props) => `${calculateIconSize(props.column)}rem`,
          padding: 2,
        },
        cursor: "pointer",
        marginBottom: "5px",
        borderRadius: "2px",
        background: "#78a6be",
        "& .iconStyle": {
          padding: 5,
        },
      },
    },
  }),
);

interface Props {
  productId: string;
  product: Product | undefined;
  findProductsModalCategoryId: string;
}

const FindProductsTileHoverActions: React.FC<Props> = ({
  productId,
  product,
  findProductsModalCategoryId,
}) => {
  const fourColumnView = useMemo(() => createStaticViewContextValue(4), []);
  const classes = useStyles({ ...fourColumnView.styles });
  const intl = useIntl();
  const dispatch = useDispatch();
  const catalogId = useSelector(selectCurrentCatalogId) || "";
  const localeId = useSelector(selectCurrentLocale) || "";
  const currentStoreId = useSelector(selectCurrentStoreId);
  const showItemManagement = "true";
  const bulkUpdateEnabled =
    useSelector((state: AppState) =>
      selectConfigValue(state, "bulkUpdateEnabled"),
    ) === "true" || showItemManagement === "true";
  const findModalColorEntry = useSelector(
    (state: AppState) =>
      selectProductsColorDataForFindProducts(state)[productId],
  );

  const productTypeCode = product?.typeCode;
  const typeCode =
    productTypeCode === "ProductBean" ? "Product" : productTypeCode;
  const isVariationBaseProduct =
    product?.variationBaseId !== "" && product?.variationBaseId !== undefined;

  const colorsForColorMgmt =
    (product?.colors?.length ? product.colors : undefined) ??
    findModalColorEntry?.colors ??
    [];
  const hasColors = Boolean(
    product?.hasColors ||
      (findModalColorEntry?.colors && findModalColorEntry.colors.length > 0),
  );
  const hasManageableColors = Boolean(
    (product?.hasManageableColors && colorsForColorMgmt.length > 0) ||
      (findModalColorEntry?.hasManageableColors &&
        colorsForColorMgmt.length > 0),
  );

  const showColorMgmt =
    hasColors &&
    hasManageableColors &&
    typeCode !== "ProductBundle" &&
    typeCode !== "ProductSet";

  const showInventoryMgmt =
    isVariationBaseProduct && productTypeCode !== "Variation Product";

  const fetchSingleProductUpdatedDetails = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dispatch(
        refreshProductFromList({
          productId,
          catalogId,
          localeCode: localeId,
          storeId: currentStoreId || "",
        }),
      );
    },
    [catalogId, currentStoreId, dispatch, localeId, productId],
  );

  const openColorWayManagement = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      dispatch(
        setModalState(ColorManagementModalId, true, {
          productId,
          isFromFindProducts: true,
          findProductsModalCategoryId,
        }),
      );
    },
    [dispatch, findProductsModalCategoryId, productId],
  );

  const openItemManagement = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (bulkUpdateEnabled) {
        dispatch(
          setModalState(itemManagementModalId, true, {
            productId,
            findProductsModalCategoryId,
          }),
        );
      } else {
        dispatch(setModalState(itemManagementSFCCModalId, true, { productId }));
      }
    },
    [bulkUpdateEnabled, dispatch, findProductsModalCategoryId, productId],
  );

  return (
    <div
      className={`${FIND_PRODUCTS_TILE_HOVER_ACTIONS_CLASS} ${classes.actionContainer}`}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <IconButton
        color="menu"
        title={intl.formatMessage({
          id: "productActions.refreshIcon",
          defaultMessage: "Refresh Product",
        })}
        aria-label="RefreshProduct"
        component="div"
        onClick={fetchSingleProductUpdatedDetails}
        className="product-tile__actions"
      >
        <CachedIcon
          titleAccess={
            product?.cachedAt
              ? `Last cached at ${new Date(product.cachedAt as Date)}`
              : undefined
          }
          className="refreshIcon"
        />
      </IconButton>
      {showColorMgmt && (
        <IconButton
          color="menu"
          title={intl.formatMessage({
            id: "productActions.colorManagement",
            defaultMessage: "Color Mgmt",
          })}
          aria-label="ColorWayManagement"
          component="div"
          onClick={openColorWayManagement}
          className="product-tile__actions"
        >
          <ColorManagementIcon className="iconStyle" />
        </IconButton>
      )}
      {showInventoryMgmt && (
        <IconButton
          color="menu"
          title={intl.formatMessage({
            id: "productActions.inventoryManagement",
            defaultMessage: "Inventory Mgmt",
          })}
          aria-label="InventoryManagement"
          component="div"
          onClick={openItemManagement}
          className="product-tile__actions"
        >
          <StyledInventoryIcon />
        </IconButton>
      )}
    </div>
  );
};

export default FindProductsTileHoverActions;
